import React, { useState } from "react";
import axios from "axios";
import { CheckCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "/logo.png";

const states = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];

const grades = ['9th', '10th', '11th', '12th', 'College Freshman', 'College Sophomore', 'College Junior', 'College Senior', 'Graduate Student', 'Adult Learner'];

const SectionHeader = ({ number, title }) => (
  <div className="flex items-center gap-3 mb-6 mt-10 first:mt-0">
    <span className="w-9 h-9 bg-emerald-700 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">{number}</span>
    <h3 className="font-display text-xl font-bold text-gray-900">{title}</h3>
  </div>
);

const Label = ({ children, required }) => (
  <label className="block text-sm font-medium text-gray-700 mb-1">
    {children} {required && <span className="text-red-500">*</span>}
  </label>
);

const Input = ({ name, value, onChange, type = "text", placeholder = "", required = false, className = "" }) => (
  <input
    type={type}
    name={name}
    value={value}
    onChange={onChange}
    required={required}
    placeholder={placeholder}
    className={`w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all ${className}`}
    data-testid={`intake-${name}`}
  />
);

const IntakeForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [student, setStudent] = useState({
    full_name: "", dob: "", age: "", school: "", grade: "", email: "",
    social_handles: "", special_pathway: "", cumulative_gpa: "",
    extracurriculars_sports: "", extracurriculars_music: "", extracurriculars_arts: "", extracurriculars_other: "",
    psat_score: "", sat_score: "", act_score: "",
    community_volunteering: "", community_service_hours: ""
  });

  const [parent1, setParent1] = useState({
    name: "", relationship: "Parent", street: "", city: "", state: "", zip: "",
    phone: "", email: "", dob: "", employer_name: "", job_title: "",
    employer_phone: "", employer_email: "",
    committed_to_paying: "", extraordinary_circumstances: ""
  });

  const [parent2, setParent2] = useState({
    name: "", relationship: "Parent", street: "", city: "", state: "", zip: "",
    phone: "", email: "", dob: "", employer_name: "", job_title: "",
    employer_phone: "", employer_email: "",
    committed_to_paying: "", extraordinary_circumstances: ""
  });

  const [custodialParent, setCustodialParent] = useState("");
  const [custodialApplicable, setCustodialApplicable] = useState("no");

  const handleStudentChange = (e) => setStudent({ ...student, [e.target.name]: e.target.value });
  const handleParent1Change = (e) => setParent1({ ...parent1, [e.target.name]: e.target.value });
  const handleParent2Change = (e) => setParent2({ ...parent2, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/intake`, {
        student,
        parent1,
        parent2: parent2.name ? parent2 : null,
        custodial_applicable: custodialApplicable === "yes",
        custodial_parent: custodialParent
      });
      setSubmitted(true);
    } catch (err) {
      setError("There was an error submitting the form. Please try again or email sheila@mypath2prosperity.com directly.");
    }
    setIsSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-emerald-50/30 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-12 max-w-lg text-center border border-amber-100" data-testid="intake-success">
          <CheckCircle className="mx-auto mb-6 text-emerald-600" size={64} />
          <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
          <p className="text-gray-600 text-lg mb-6">
            Your Family Intake Form has been submitted successfully. Sheila will review your information and contact you within 24–48 hours.
          </p>
          <a href="/" className="btn-primary inline-block">Return to Website</a>
        </div>
      </div>
    );
  }

  const ParentSection = ({ number, title, data, onChange, parentNum }) => (
    <>
      <SectionHeader number={number} title={title} />

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label required>Full Name</Label>
          <Input name="name" value={data.name} onChange={onChange} required={parentNum === 1} />
        </div>
        <div>
          <Label>Relationship</Label>
          <select name="relationship" value={data.relationship} onChange={onChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none" data-testid={`intake-p${parentNum}-relationship`}>
            <option value="Parent">Parent</option>
            <option value="Stepparent">Stepparent</option>
            <option value="Guardian">Guardian</option>
          </select>
        </div>
      </div>

      <div>
        <Label required={parentNum === 1}>Street Address</Label>
        <Input name="street" value={data.street} onChange={onChange} required={parentNum === 1} />
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label required={parentNum === 1}>City</Label>
          <Input name="city" value={data.city} onChange={onChange} required={parentNum === 1} />
        </div>
        <div>
          <Label required={parentNum === 1}>State</Label>
          <select name="state" value={data.state} onChange={onChange} required={parentNum === 1} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none" data-testid={`intake-p${parentNum}-state`}>
            <option value="">Select</option>
            {states.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <Label required={parentNum === 1}>ZIP Code</Label>
          <Input name="zip" value={data.zip} onChange={onChange} required={parentNum === 1} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label required={parentNum === 1}>Phone Number</Label>
          <Input name="phone" type="tel" value={data.phone} onChange={onChange} required={parentNum === 1} />
        </div>
        <div>
          <Label required={parentNum === 1}>Email Address</Label>
          <Input name="email" type="email" value={data.email} onChange={onChange} required={parentNum === 1} />
        </div>
      </div>

      <div>
        <Label>Date of Birth</Label>
        <Input name="dob" type="date" value={data.dob} onChange={onChange} />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Job Title</Label>
          <Input name="job_title" value={data.job_title} onChange={onChange} />
        </div>
        <div>
          <Label>Employer Name</Label>
          <Input name="employer_name" value={data.employer_name} onChange={onChange} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Employer Phone</Label>
          <Input name="employer_phone" type="tel" value={data.employer_phone} onChange={onChange} />
        </div>
        <div>
          <Label>Employer Email</Label>
          <Input name="employer_email" type="email" value={data.employer_email} onChange={onChange} />
        </div>
      </div>

      <div>
        <Label required={parentNum === 1}>Committed to paying for school?</Label>
        <div className="flex gap-4 mt-1">
          {["Yes", "No", "Partially"].map(opt => (
            <label key={opt} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={`committed_to_paying`}
                value={opt}
                checked={data.committed_to_paying === opt}
                onChange={onChange}
                className="text-emerald-700 focus:ring-emerald-500"
              />
              <span className="text-gray-700">{opt}</span>
            </label>
          ))}
        </div>
      </div>

      {(data.committed_to_paying === "No" || data.committed_to_paying === "Partially") && (
        <div>
          <Label>Extraordinary Circumstances</Label>
          <textarea
            name="extraordinary_circumstances"
            value={data.extraordinary_circumstances}
            onChange={onChange}
            rows={3}
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none"
            placeholder="Please describe any financial hardships or extraordinary circumstances..."
            data-testid={`intake-p${parentNum}-circumstances`}
          />
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-emerald-50/30 py-8 px-4" data-testid="intake-form-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <img src={LOGO_URL} alt="Pathways to Prosperity" className="h-16 w-auto" />
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">Pathways to Prosperity</h1>
            <h2 className="text-emerald-700 font-semibold text-lg">Family Intake Form</h2>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-lg border border-amber-100 p-6 sm:p-10">
          <p className="text-gray-600 mb-8 leading-relaxed">
            Please complete this form as thoroughly as possible so that Sheila can make the best use of your consultation time together. All information is kept strictly confidential.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">

            {/* ──── Student Information ──── */}
            <SectionHeader number="1" title="Student Information" />

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label required>Student's Full Name</Label>
                <Input name="full_name" value={student.full_name} onChange={handleStudentChange} required />
              </div>
              <div>
                <Label required>Date of Birth</Label>
                <Input name="dob" type="date" value={student.dob} onChange={handleStudentChange} required />
              </div>
              <div>
                <Label required>Age</Label>
                <Input name="age" type="number" value={student.age} onChange={handleStudentChange} required />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label required>Current School</Label>
                <Input name="school" value={student.school} onChange={handleStudentChange} required />
              </div>
              <div>
                <Label required>Grade</Label>
                <select name="grade" value={student.grade} onChange={handleStudentChange} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none" data-testid="intake-grade">
                  <option value="">Select grade</option>
                  {grades.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label required>Student Email</Label>
                <Input name="email" type="email" value={student.email} onChange={handleStudentChange} required />
              </div>
              <div>
                <Label>Social Media Handles</Label>
                <Input name="social_handles" value={student.social_handles} onChange={handleStudentChange} placeholder="e.g., @handle (Instagram, TikTok, etc.)" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Special Pathway in High School</Label>
                <Input name="special_pathway" value={student.special_pathway} onChange={handleStudentChange} placeholder="e.g., IB, AP, Honors, CTE, Dual Enrollment" />
              </div>
              <div>
                <Label required>Cumulative GPA</Label>
                <Input name="cumulative_gpa" value={student.cumulative_gpa} onChange={handleStudentChange} required placeholder="e.g., 3.5" />
              </div>
            </div>

            {/* ──── Test Scores ──── */}
            <SectionHeader number="2" title="Standardized Test Scores (if applicable)" />

            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>PSAT Score</Label>
                <Input name="psat_score" value={student.psat_score} onChange={handleStudentChange} placeholder="e.g., 1200" />
              </div>
              <div>
                <Label>SAT Score</Label>
                <Input name="sat_score" value={student.sat_score} onChange={handleStudentChange} placeholder="e.g., 1350" />
              </div>
              <div>
                <Label>ACT Score</Label>
                <Input name="act_score" value={student.act_score} onChange={handleStudentChange} placeholder="e.g., 28" />
              </div>
            </div>

            {/* ──── Extracurriculars ──── */}
            <SectionHeader number="3" title="Extracurricular Activities" />

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Sports</Label>
                <textarea name="extracurriculars_sports" value={student.extracurriculars_sports} onChange={handleStudentChange} rows={2} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none" placeholder="e.g., Varsity Soccer (Captain), Track & Field" data-testid="intake-sports" />
              </div>
              <div>
                <Label>Music</Label>
                <textarea name="extracurriculars_music" value={student.extracurriculars_music} onChange={handleStudentChange} rows={2} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none" placeholder="e.g., Concert Band (Flute), Choir" data-testid="intake-music" />
              </div>
              <div>
                <Label>Arts</Label>
                <textarea name="extracurriculars_arts" value={student.extracurriculars_arts} onChange={handleStudentChange} rows={2} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none" placeholder="e.g., Drama Club, Painting, Photography" data-testid="intake-arts" />
              </div>
              <div>
                <Label>Other Activities / Clubs</Label>
                <textarea name="extracurriculars_other" value={student.extracurriculars_other} onChange={handleStudentChange} rows={2} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none" placeholder="e.g., Student Government, Debate, Robotics" data-testid="intake-other" />
              </div>
            </div>

            {/* ──── Community Service ──── */}
            <SectionHeader number="4" title="Community Involvement" />

            <div>
              <Label>Community Volunteering / Service Activities</Label>
              <textarea name="community_volunteering" value={student.community_volunteering} onChange={handleStudentChange} rows={3} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none" placeholder="Describe any volunteer work, community organizations, church activities, etc." data-testid="intake-volunteering" />
            </div>

            <div>
              <Label>Required Community Service Hours (if applicable)</Label>
              <Input name="community_service_hours" value={student.community_service_hours} onChange={handleStudentChange} placeholder="e.g., 100 hours completed / 40 hours remaining" />
            </div>

            {/* ──── Parent/Guardian 1 ──── */}
            <ParentSection number="5" title="Parent / Guardian 1" data={parent1} onChange={handleParent1Change} parentNum={1} />

            {/* ──── Parent/Guardian 2 ──── */}
            <ParentSection number="6" title="Parent / Guardian 2 / Stepparent (if applicable)" data={parent2} onChange={handleParent2Change} parentNum={2} />

            {/* ──── Custodial Parent ──── */}
            <SectionHeader number="7" title="Custodial Information" />

            <div>
              <Label>Is there a custodial parent arrangement?</Label>
              <div className="flex gap-4 mt-1">
                {["yes", "no"].map(opt => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="custodial_applicable" value={opt} checked={custodialApplicable === opt} onChange={(e) => setCustodialApplicable(e.target.value)} className="text-emerald-700 focus:ring-emerald-500" />
                    <span className="text-gray-700 capitalize">{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            {custodialApplicable === "yes" && (
              <div>
                <Label>Which parent has custodial custody?</Label>
                <Input name="custodial_parent" value={custodialParent} onChange={(e) => setCustodialParent(e.target.value)} placeholder="e.g., Parent 1 — Jane Doe" />
              </div>
            )}

            {/* ──── Submit ──── */}
            {error && (
              <div className="p-4 rounded-xl bg-red-50 text-red-700" data-testid="intake-error">{error}</div>
            )}

            <div className="pt-6 border-t border-gray-100">
              <p className="text-sm text-gray-500 mb-4">
                All information provided is strictly confidential and will only be used by Sheila Coleman-Castells for educational counseling purposes.
              </p>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-emerald-700 text-white font-semibold text-lg rounded-xl hover:bg-emerald-800 transition-colors disabled:opacity-50"
                data-testid="intake-submit-btn"
              >
                {isSubmitting ? "Submitting..." : "Submit Intake Form"}
              </button>
            </div>
          </form>
        </div>

        <p className="text-center text-gray-400 text-sm mt-6">
          &copy; {new Date().getFullYear()} Pathways to Prosperity, LLC. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default IntakeForm;
